import { Link, useParams, Navigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();

  const category = categories.find((c) => c.id === slug);

  if (!category || !slug) {
    return <Navigate to="/" replace />;
  }

  const categoryArticles = getArticlesByCategory(slug);

  const categoryJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 亲密发声训练指南`,
    description: category.description,
    url: `https://auxnuyb.cn/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '亲密发声训练指南',
      url: 'https://auxnuyb.cn',
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '首页',
          item: 'https://auxnuyb.cn',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: category.name,
          item: `https://auxnuyb.cn/category/${slug}`,
        },
      ],
    },
  };

  return (
    <>
      <SEOHead
        title={`${category.name} - 亲密发声训练`}
        description={category.description}
        keywords={`亲密发声,${category.name},亲密发声技巧,情侣声音表达,成人情感沟通`}
        canonicalPath={`/category/${slug}`}
        jsonLd={categoryJsonLd}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        {/* Category Header */}
        <section className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb />
            <div className="py-8 md:py-12">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <i className={`${category.icon} text-xl`}></i>
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                    {category.name}
                  </h1>
                  <p className="text-sm text-foreground-500 mt-1">
                    {categoryArticles.length} 篇文章
                  </p>
                </div>
              </div>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl">
                {category.description}
              </p>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="border-b border-background-200/70 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-3">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.id}`}
                  className={`px-4 py-2 text-sm rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                    cat.id === slug
                      ? 'bg-primary-500 text-background-50 font-medium'
                      : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Articles List */}
        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto">
            {categoryArticles.length > 0 ? (
              <div className="flex flex-col gap-4 md:gap-5">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group flex flex-col sm:flex-row gap-4 md:gap-5 p-4 md:p-5 rounded-lg bg-background-100 border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                    data-product-shop
                  >
                    <div className="w-full sm:w-48 md:w-56 h-40 sm:h-auto flex-shrink-0 rounded-md overflow-hidden">
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="flex-1 flex flex-col justify-between min-w-0">
                      <div>
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className="text-xs text-foreground-500 whitespace-nowrap">{article.date}</span>
                          <span className="text-xs text-foreground-400">·</span>
                          <span className="text-xs text-foreground-500 whitespace-nowrap">{article.readTime}</span>
                        </div>
                        <h2 className="text-base md:text-lg font-semibold text-foreground-900 font-heading mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                          {article.title}
                        </h2>
                        <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                          {article.excerpt}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 mt-3 flex-wrap">
                        {article.tags.slice(0, 3).map((tag) => (
                          <span
                            key={tag}
                            className="px-2 py-0.5 text-xs rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap"
                          >
                            {tag}
                          </span>
                        ))}
                        <span className="text-xs text-foreground-500 ml-auto whitespace-nowrap">
                          {article.author}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
                  <i className="ri-file-search-line text-2xl"></i>
                </div>
                <p className="text-foreground-600">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}